'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { SlidersHorizontal, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTenant } from '@/components/storefront/TenantProvider';
import { buildNestApiUrl } from '@/lib/api-url';
import { vehicleImageUrl } from '@/lib/image';
import { ConditionBadge } from '@/components/inventory/ConditionBadge';

interface VehiclePhoto { storagePath: string; isPrimary: boolean; }
interface Vehicle {
  id: string; year: number; make: string; model: string; trim?: string;
  mileage: number; listPrice: number; condition: string; photos: VehiclePhoto[];
}
interface Meta { total: number; page: number; pages: number; limit: number; }

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest First' },
  { value: 'listPrice:asc', label: 'Price: Low → High' },
  { value: 'listPrice:desc', label: 'Price: High → Low' },
  { value: 'year:desc', label: 'Year: Newest' },
  { value: 'year:asc', label: 'Year: Oldest' },
  { value: 'mileage:asc', label: 'Mileage: Lowest' },
];

export default function StorefrontInventoryPage() {
  const tenant = useTenant();
  const searchParams = useSearchParams();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [meta, setMeta] = useState<Meta>({ total: 0, page: 1, pages: 1, limit: 24 });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Read filter state from URL
  const getParam = (key: string) => searchParams.get(key) ?? '';
  const [search, setSearch] = useState(getParam('search'));
  const [make, setMake] = useState(getParam('make'));
  const [model, setModel] = useState(getParam('model'));
  const [yearMin, setYearMin] = useState(getParam('year_min'));
  const [yearMax, setYearMax] = useState(getParam('year_max'));
  const [priceMin, setPriceMin] = useState(getParam('price_min'));
  const [priceMax, setPriceMax] = useState(getParam('price_max'));
  const [mileageMax, setMileageMax] = useState(getParam('mileage_max'));
  const [condition, setCondition] = useState(getParam('condition'));
  const [sortVal, setSortVal] = useState(
    getParam('sort') ? `${getParam('sort')}:${getParam('order') || 'desc'}` : 'createdAt:desc',
  );
  const [page, setPage] = useState(parseInt(getParam('page') || '1', 10));

  const activeFilterCount = [make, model, yearMin, yearMax, priceMin, priceMax, mileageMax, condition].filter(Boolean).length;

  const buildParams = useCallback(() => {
    const [sort, order] = sortVal.split(':');
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (make) p.set('make', make);
    if (model) p.set('model', model);
    if (yearMin) p.set('year_min', yearMin);
    if (yearMax) p.set('year_max', yearMax);
    if (priceMin) p.set('price_min', priceMin);
    if (priceMax) p.set('price_max', priceMax);
    if (mileageMax) p.set('mileage_max', mileageMax);
    if (condition) p.set('condition', condition);
    p.set('sort', sort);
    p.set('order', order);
    p.set('page', String(page));
    return p;
  }, [search, make, model, yearMin, yearMax, priceMin, priceMax, mileageMax, condition, sortVal, page]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const res = await fetch(
        buildNestApiUrl(`/storefront/${tenant.slug}/vehicles?${params}`),
      );
      const json = await res.json() as { success: boolean; data: Vehicle[]; meta: Meta };
      if (json.success) { setVehicles(json.data); setMeta(json.meta); }
    } catch {
      // network error
    } finally {
      setLoading(false);
    }
  }, [buildParams, tenant.slug]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const resetFilters = () => {
    setSearch('');
    setMake('');
    setModel('');
    setYearMin('');
    setYearMax('');
    setPriceMin('');
    setPriceMax('');
    setMileageMax('');
    setCondition('');
    setSortVal('createdAt:desc');
    setPage(1);
  };

  const inp =
    'w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]';

  const hasAnyFilter =
    !!search ||
    !!make ||
    !!model ||
    !!yearMin ||
    !!yearMax ||
    !!priceMin ||
    !!priceMax ||
    !!mileageMax ||
    !!condition ||
    sortVal !== 'createdAt:desc';

  const FiltersPanel = () => (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-900">Filters</p>
        {activeFilterCount > 0 && (
          <button onClick={resetFilters} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
            <X className="h-3 w-3" /> Clear all
          </button>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Make</label>
        <input value={make} onChange={(e) => { setMake(e.target.value); setPage(1); }} placeholder="e.g. Toyota" className={inp} />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Model</label>
        <input value={model} onChange={(e) => { setModel(e.target.value); setPage(1); }} placeholder="e.g. Camry" className={inp} />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Year Range</label>
        <div className="flex gap-2">
          <input value={yearMin} onChange={(e) => { setYearMin(e.target.value); setPage(1); }} placeholder="Min" type="number" className={inp} />
          <input value={yearMax} onChange={(e) => { setYearMax(e.target.value); setPage(1); }} placeholder="Max" type="number" className={inp} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Price Range ($)</label>
        <div className="flex gap-2">
          <input value={priceMin} onChange={(e) => { setPriceMin(e.target.value); setPage(1); }} placeholder="Min" type="number" className={inp} />
          <input value={priceMax} onChange={(e) => { setPriceMax(e.target.value); setPage(1); }} placeholder="Max" type="number" className={inp} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Max Mileage</label>
        <input value={mileageMax} onChange={(e) => { setMileageMax(e.target.value); setPage(1); }} placeholder="e.g. 50000" type="number" className={inp} />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-2">Condition</label>
        <div className="space-y-1.5">
          {['NEW', 'USED', 'CERTIFIED'].map((c) => (
            <label key={c} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={condition === c}
                onChange={() => { setCondition(condition === c ? '' : c); setPage(1); }}
                className="rounded"
                style={{ accentColor: 'var(--color-primary)' }}
              />
              <span className="text-sm text-slate-700 capitalize">{c === 'CERTIFIED' ? 'Certified Pre-Owned' : c.charAt(0) + c.slice(1).toLowerCase()}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      {/* Top bar */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search make, model, VIN…"
          className="flex-1 min-w-48 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
        <select
          value={sortVal}
          onChange={(e) => { setSortVal(e.target.value); setPage(1); }}
          className="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          onClick={() => setFiltersOpen(true)}
          className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm sm:hidden"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="rounded-full px-1.5 py-0.5 text-xs text-white" style={{ backgroundColor: 'var(--color-primary)' }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <p className="text-sm text-slate-400">{meta.total.toLocaleString()} vehicles</p>
      </div>

      {hasAnyFilter && (
        <div className="mb-4">
          <button
            onClick={resetFilters}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Clear all filters
          </button>
        </div>
      )}

      <div className="flex gap-8">
        {/* Sidebar filters — desktop */}
        <aside className="hidden w-56 shrink-0 sm:block">
          <FiltersPanel />
        </aside>

        {/* Grid */}
        <div className="flex-1 min-w-0">
          {loading && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-slate-200 overflow-hidden animate-pulse">
                  <div className="aspect-video bg-slate-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 w-3/4 bg-slate-200 rounded" />
                    <div className="h-3 w-1/2 bg-slate-100 rounded" />
                    <div className="h-5 w-1/3 bg-slate-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && vehicles.length === 0 && (
            <div className="flex flex-col items-center gap-4 py-20 text-center">
              <div className="text-5xl">🔍</div>
              <p className="text-lg font-semibold text-slate-700">No vehicles match your search</p>
              <p className="text-sm text-slate-400">Try adjusting your filters or search terms.</p>
              <button onClick={resetFilters} className="rounded-full px-5 py-2 text-sm text-white" style={{ backgroundColor: 'var(--color-primary)' }}>
                Clear Filters
              </button>
            </div>
          )}

          {!loading && vehicles.length > 0 && (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {vehicles.map((v) => {
                const photo = v.photos.find((p) => p.isPrimary) ?? v.photos[0];
                const imgUrl = photo ? vehicleImageUrl(photo.storagePath, 'card') : null;
                return (
                  <Link
                    key={v.id}
                    href={`/inventory/${v.id}`}
                    data-testid="vehicle-card"
                    className="group rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="relative aspect-video overflow-hidden bg-slate-100">
                      {imgUrl ? (
                        <Image
                          src={imgUrl}
                          alt={`${v.year} ${v.make} ${v.model}${v.trim ? ` ${v.trim}` : ''}`}
                          fill
                          className="object-cover transition-transform duration-300 group-hover:scale-105"
                          sizes="(max-width: 640px) 100vw, 33vw"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-slate-300 text-sm">No Photo</div>
                      )}
                      <div className="absolute top-2 left-2">
                        <ConditionBadge condition={v.condition} />
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="font-semibold text-slate-900">{v.year} {v.make} {v.model}</p>
                      {v.trim && <p className="text-xs text-slate-400">{v.trim}</p>}
                      <p className="mt-1 text-sm text-slate-500">{v.mileage.toLocaleString()} miles</p>
                      <div className="mt-3 flex items-center justify-between">
                        <p className="text-lg font-bold text-slate-900">${Number(v.listPrice).toLocaleString()}</p>
                        <span
                          className="rounded-full px-3 py-1 text-xs font-medium text-white"
                          style={{ backgroundColor: 'var(--color-primary)' }}
                        >
                          View Details
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {meta.pages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-slate-600">{meta.page} of {meta.pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                disabled={page === meta.pages}
                className="rounded-lg border border-slate-200 p-2 hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {filtersOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 sm:hidden" onClick={() => setFiltersOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-white p-5 shadow-xl sm:hidden">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-semibold text-slate-900">Filters</p>
              <button onClick={() => setFiltersOpen(false)}><X className="h-5 w-5 text-slate-500" /></button>
            </div>
            <FiltersPanel />
            <button
              onClick={() => setFiltersOpen(false)}
              className="mt-6 w-full rounded-full py-2.5 text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              Show {meta.total} results
            </button>
          </div>
        </>
      )}
    </div>
  );
}
